import React, {useEffect, useState} from "react";
import {Box, Button, FormControl, Grid2, InputLabel, MenuItem, Select} from "@mui/material";
import {Team} from "../../types/teams.types";
import {Group} from "../../types/groups.types";
import {UserActivityTypes} from "../../types";
import {LoadingButton} from "@mui/lab";
import {CustomDatePicker} from "../datepicker";
import dayjs, { Dayjs } from 'dayjs';
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import palette from "../../theme/palette";

interface Props {
    allGroups : Group[],
    onFilter : (filter : UserActivityTypes.UserActivityFilter) => void,
}
const buttonColor = palette.darkBlue
const intervalOptions = [
    {id: 'last7Days', label: 'Last 7 Days'},
    {id: 'today', label: 'Today'},
    {id: 'yesterday', label: 'Yesterday'},
    {id: 'last14Days', label: 'Last 14 Days'},
    {id: 'last30Days', label: 'Last 30 Days'},
    {id: 'thisMonth', label: 'This Month'},
    {id: 'lastMonth', label: 'Last Month'},
    {id: 'custom', label: 'Custom'}
];

export const UserActivityFilters: React.FC<Props> = ({allGroups, onFilter}) => {

    const { loading } = useSelector((state: RootState) => state.userActivity);

    const [selectedInterval, setSelectedInterval] = useState<string>('last7Days');

    const initStartDate = dayjs().subtract(7, 'day').startOf('day')
    const initEndDate = dayjs().endOf('day')

    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [allTeams, setAllTeams] = useState<Team[]>([]);
    const [selectedTeams, setSelectedTeams] = useState<Team[] | string[]>(allTeams ?? []);
    const [startDate, setStartDate] = React.useState<Dayjs | null>(initStartDate);
    const [endDate, setEndDate] = React.useState<Dayjs | null>(initEndDate);


    let initialTeams = allGroups?.map((group) => group.teams)?.flat()

    useEffect(() => {
        setAllTeams(initialTeams)
    }, [allGroups]);

    useEffect(() => {
        setAllTeams(selectedGroup ? selectedGroup.teams : initialTeams);
    }, [selectedGroup]);

    const onIntervalChange= (intervalId : string) => {
        setSelectedInterval(intervalId);
        const today = dayjs();
        switch (intervalId) {
            case 'last7Days':
                setStartDate(today.subtract(7, 'day'));
                setEndDate(today);
                break;
            case 'today':
                setStartDate(today.startOf('day'));
                setEndDate(today.endOf('day'));
                break;
            case 'yesterday':
                setStartDate(today.subtract(1, 'day').startOf('day'));
                setEndDate(today.subtract(1, 'day').endOf('day'));
                break;
            case 'last14Days':
                setStartDate(today.subtract(14, 'day'));
                setEndDate(today);
                break;
            case 'last30Days':
                setStartDate(today.subtract(30, 'day'));
                setEndDate(today);
                break;
            case 'thisMonth':
                setStartDate(today.startOf('month'));
                setEndDate(today.endOf('month'));
                break;
            case 'lastMonth':
                setStartDate(today.subtract(1, 'month').startOf('month'));
                setEndDate(today.subtract(1, 'month').endOf('month'));
                break;
            case 'custom':
                setStartDate(null);
                setEndDate(null);
                break;
            default:
                setStartDate(null);
                setEndDate(null);
        }
    }

    const handleTeamsChange = (event : any) => {
        const {
            target: { value },
        } = event;

        const allIndex = value.indexOf('All');
        if (allIndex !== -1) {
            //remove 'All' case
            if (value.length > 1 && allIndex === 0) {
                // Remove 'All' from the value array
                let values = value.filter((item: string) => item !== 'All')
                setSelectedTeams(values);
            } else {
                // Keep only 'All' in the value array
                setSelectedTeams([]);
            }
        }else
            setSelectedTeams(value);
    };
    return (
        <Grid2 container spacing={4}>
            <Grid2 size={{md:2,xs:12,sm:12}}>
                <FormControl sx={{width:'100%'}}>
                    <InputLabel sx={{mb:1,
                        '&.Mui-focused': {
                            color: 'text.primary' // color when focused
                        }}}>Group
                    </InputLabel>
                    <Select
                        labelId="group-select-label"
                        id="groupSelect"
                        value={selectedGroup?.id ?? 'all'}
                        label="Group"
                        onChange={(event)=>{
                            let group = allGroups.find(group => group.id === event.target.value)!
                            setSelectedGroup(group);
                            setSelectedTeams([])
                        }}
                    >
                        <MenuItem key={'all'} value={'all'}>
                            All Groups
                        </MenuItem>
                        {allGroups.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid2>
            <Grid2 size={{md:2,xs:12,sm:12}}>
                <FormControl sx={{width:'100%'}}>
                    <InputLabel sx={{mb:1,
                        '&.Mui-focused': {
                            color: 'text.primary' // color when focused
                        }}}>Teams
                    </InputLabel>
                    <Select
                        multiple={true}
                        labelId="team-select-label"
                        id="team-multi-select"
                        value={selectedTeams?.length ? selectedTeams : ['All']}
                        label="Teams"
                        onChange={handleTeamsChange}
                    >
                        <MenuItem key={'all'} value={'All'}>
                            All Teams
                        </MenuItem>

                        {!selectedGroup && (
                            <MenuItem key={'none'} value={'Unassigned'}>
                                No Team Assigned
                            </MenuItem>
                        )}

                        {allTeams.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid2>
            <Grid2 size={{md:2,xs:12,sm:12}}>
                <FormControl sx={{width:'100%'}}>
                    <InputLabel sx={{mb:1,
                        '&.Mui-focused': {
                            color: 'text.primary' // color when focused
                        }}}>Date Range
                    </InputLabel>
                    <Select
                        labelId="interval-select-label"
                        id="intervalSelect"
                        value={selectedInterval || ''}
                        label="Date Range"
                        onChange={(event)=>{
                           onIntervalChange(event.target.value)
                        }}
                    >
                        {intervalOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid2>
           {selectedInterval === 'custom' && (
                <Grid2 size={{md:4,xs:12,sm:12}}>
                    <Grid2 display={'flex'} flexDirection={'row'} spacing={2}>
                        <Box sx={{ mr: 2 }}>
                            <CustomDatePicker date={startDate} setDate={setStartDate} label={'MM/DD/YYYY'}/>
                        </Box>
                        <Box>
                            <CustomDatePicker date={endDate} setDate={setEndDate} label={'MM/DD/YYYY'}/>
                        </Box>
                    </Grid2>
                </Grid2>
            )}
            <Grid2 display={'flex'} flexDirection={'row'} sx={{p:1}}>
                <LoadingButton
                    variant='contained'
                    sx={{backgroundColor: buttonColor, paddingX: 2.5, fontSize : 16, fontWeight: 400 }}
                    loading={loading.fetchActiveUserData}
                    type={'submit'}
                    onClick={()=>{
                        let groupId = selectedGroup?.id ?? ((selectedTeams?.length === 0 || selectedTeams?.some(team => typeof team === 'string' && team === 'Unassigned')) ? null : 'dummyGroupId');
                        let teamIds = selectedTeams?.length ? selectedTeams.map((team) => {
                            if(typeof team === 'string') {
                                return team;
                            }else
                                return team?.id;
                        }) : allTeams?.map((team) => team.id);
                        onFilter({
                            startDate : startDate?.format('YYYY-MM-DD HH:MM'),
                            endDate : endDate?.format('YYYY-MM-DD HH:MM'),
                            teamIds,
                            groupId,
                        })
                    }}
                >
                    Filter
                </LoadingButton>
                <Button
                    disabled={loading.fetchActiveUserData}
                    variant='text'
                    sx={{paddingX: 2.5, color : 'text.secondary', fontSize : 16, fontWeight: 400}}
                    onClick={()=>{
                        setStartDate(initStartDate)
                        setEndDate(initEndDate)
                        setSelectedGroup(null)
                        setSelectedTeams([])
                        setSelectedInterval('last7Days')
                        setAllTeams(allGroups?.map((group) => group.teams)?.flat())
                        let teamIds = initialTeams?.map((team) => team.id)
                        teamIds?.push('Unassigned')
                        onFilter({
                            startDate : initStartDate.format('YYYY-MM-DD HH:MM'),
                            endDate : initEndDate.format('YYYY-MM-DD HH:MM'),
                            teamIds : teamIds,
                            groupId : null
                        })
                    }}
                >
                    Clear
                </Button>
            </Grid2>
        </Grid2>
    )
}
